import { tool, type ToolDescriptor } from "../tool";
import { giraffeSdkDescribe } from "./sdk";

/**
 * `giraffe_status`, the one relay tool that needs no introduction.
 *
 * `giraffe_status` is `docs/tool-surface.md`'s unlock loop in one tool: it is present in every
 * state, including the state where nothing is connected, because **an empty
 * tool list looks like a broken server** and Claude then has no reason to
 * mention Giraffe at all — so the user never learns what to open.
 */
const giraffeStatus: ToolDescriptor = tool({
  name: "giraffe_status",
  description:
    "Whether a Giraffe browser session is connected to this account and, if not, exactly " +
    "what to open. Also reports which workspace is the active target and which apps are " +
    "live in it. Call this first when any other Giraffe tool reports no session.",
  inputSchema: { type: "object", properties: {}, additionalProperties: false },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
});

/**
 * The dispatcher's discovery half (the dispatcher change).
 *
 * ## Why app tools are no longer in `tools/list`
 *
 * They varied with what the user had open, and claude.ai re-fetches `tools/list`
 * only at connector setup and on a manual refresh (measured 2026-09-10). So a
 * user who paired mid-conversation, or opened a second app panel, held a list
 * that was correct when fetched and wrong by the time it was used — and the only
 * repair was a trip to settings. Moving app tools behind a dispatcher removes the
 * premise rather than chasing the cache: `tools/list` is now the same bytes for
 * every user in every session state, so a stale copy has nothing to be stale
 * about.
 *
 * The cost is that the client no longer renders an app tool's own name or its
 * annotations — see `docs/tool-surface.md`'s amended *annotations* paragraph. They still travel, in
 * this tool's result, and are read by the MODEL.
 *
 * ## Why it is a separate tool from `giraffe_app_call`
 *
 * A single tool that both lists and calls returns two different result shapes
 * from one name, which is the precise thing `docs/tool-surface.md`'s governing rule exists to
 * prevent. Two names, two shapes.
 */
const giraffeAppTools: ToolDescriptor = tool({
  name: "giraffe_app_tools",
  description:
    "List the tools provided by the Giraffe apps currently open in the user's browser, with " +
    "their full input schemas. Apps bring their own tools, so these are NOT in the main tool " +
    "list and are not knowable in advance. Call this before calling giraffe_app_call, and call " +
    "it again rather than reusing an earlier result — the set changes as the user opens and " +
    "closes app panels. An empty list means no app is open, not that something failed.",
  inputSchema: { type: "object", properties: {}, additionalProperties: false },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
});

/**
 * The dispatcher's invoke half.
 *
 * ## `destructiveHint` is TRUE, and that is not pessimism
 *
 * This one name can reach every tool an app declares, including one that writes
 * to the user's project. Under P6 the client's permission prompt is the entire
 * safety story, and it renders THIS tool's annotation — never the app tool's,
 * which it no longer sees. A dispatcher that can write and calls itself
 * read-only is a wrong answer wearing a correct one's clothes.
 *
 * The cost is real and is accepted: a read-only app tool now prompts as though
 * it writes. The per-app scoping surface (#16) is where that gets refined, by
 * letting the user decide per app rather than per call.
 */
const giraffeAppCall: ToolDescriptor = tool({
  name: "giraffe_app_call",
  description:
    "Call one of the tools returned by giraffe_app_tools. Pass the tool's exact name and its " +
    "arguments. Arguments are validated against that tool's own schema, so call " +
    "giraffe_app_tools first to read it — a guessed name or shape is rejected at the relay and " +
    "never reaches the browser. Some app tools write to the user's project; the result of " +
    "giraffe_app_tools says which.",
  inputSchema: {
    type: "object",
    properties: {
      tool: {
        type: "string",
        description:
          'The exact tool name from giraffe_app_tools, including its app prefix (e.g. "myapp__ping").',
      },
      arguments: {
        type: "object",
        description:
          "Arguments for that tool, matching the inputSchema giraffe_app_tools returned.",
      },
    },
    required: ["tool"],
    additionalProperties: false,
  },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
  destructiveHint: true,
});

export const RELAY_TOOLS: readonly ToolDescriptor[] = [
  giraffeStatus,
  giraffeAppTools,
  giraffeAppCall,
  // Answered here rather than in a frame: its entire body reads
  // `HOST_RPC_SIGNATURES`, a generated constant, so it needs no browser — and
  // it therefore answers in EVERY session state, with no panel open. That is
  // the point rather than a side effect: an agent reads the host's RPC surface
  // while it is deciding what to ask for, which is before the user has opened
  // anything. It is the first fixed tool other than `giraffe_status` that a
  // `no_session` does not block.
  giraffeSdkDescribe,
];

/** Answered by `relay/core.ts` from the live session, never from a cache. */
export const APP_TOOLS_NAME = "giraffe_app_tools";
export const APP_CALL_NAME = "giraffe_app_call";
